import { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import { prospectsAPI, wallAPI } from '@/lib/api';
import { WallItemDialog } from './WallItemDialog';
import { HashtagPicker } from './HashtagPicker';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { AtSign, Send, Loader2, MessageSquareText, Trash2, Zap, HardHat, CheckSquare, Users, CalendarClock, AlertTriangle, Pencil, X, BookmarkPlus, LayoutTemplate, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { linkifyText } from '@/lib/linkify';
import { WallAttachmentComposer } from './attachments/WallAttachmentComposer';
import { WallAttachmentList } from './attachments/WallAttachmentList';
import { WallMessageFrame } from './attachments/WallMessageFrame';
import { automaticRelanceDisplay } from './automaticRelanceDisplay';

// Statut LIVE de la tâche liée (calculé côté serveur à la lecture du mur)
const TASK_STATUS_BADGE = {
    todo: { label: 'À faire', cls: 'bg-amber-500 text-white' },
    postponed: { label: 'Reportée', cls: 'bg-slate-200 text-slate-700' },
    done: { label: 'Terminée', cls: 'bg-emerald-100 text-emerald-700' },
    deleted: { label: 'Supprimée', cls: 'bg-red-100 text-red-700 line-through' },
};
import { Paperclip } from 'lucide-react';
import { publicationKey, getPublication, forgetPublication, subscribePublication, updatePublication } from './attachments/publicationState';
const WallAttachmentViewer = lazy(() => import('./attachments/WallAttachmentViewer'));

// Mini-renderer markdown pour l'état des lieux Uatu.
// Le format est CONTRÔLÉ côté backend (### titres, listes -, **bold**).
// Suffisant pour cet usage précis — évite d'installer react-markdown.
const UatuMarkdown = ({ content }) => {
    if (!content) return null;
    const lines = content.split('\n');
    const blocks = [];
    let listBuffer = [];
    const flushList = () => {
        if (listBuffer.length > 0) {
            blocks.push({ type: 'ul', items: [...listBuffer] });
            listBuffer = [];
        }
    };
    lines.forEach((line) => {
        const trimmed = line.trim();
        if (trimmed.startsWith('### ')) {
            flushList();
            blocks.push({ type: 'h4', text: trimmed.slice(4) });
        } else if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
            listBuffer.push(trimmed.slice(2));
        } else if (trimmed === '') {
            flushList();
            blocks.push({ type: 'br' });
        } else {
            flushList();
            blocks.push({ type: 'p', text: trimmed });
        }
    });
    flushList();
    // Rend une ligne avec **bold** inline
    const renderInline = (text) => {
        const parts = text.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((p, i) =>
            p.startsWith('**') && p.endsWith('**')
                ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
                : <span key={i}>{p}</span>
        );
    };
    return (
        <div className="mt-2 space-y-1.5 text-sm text-slate-800 leading-relaxed">
            {blocks.map((b, idx) => {
                if (b.type === 'h4') return <h4 key={idx} className="text-sm font-bold text-purple-900 mt-3 first:mt-1">{renderInline(b.text)}</h4>;
                if (b.type === 'ul') return (
                    <ul key={idx} className="list-disc list-inside space-y-0.5 ml-1">
                        {b.items.map((item, i) => <li key={i}>{renderInline(item)}</li>)}
                    </ul>
                );
                if (b.type === 'br') return null;
                return <p key={idx}>{renderInline(b.text)}</p>;
            })}
        </div>
    );
};

const initials = (name) => {
    if (!name) return '?';
    const p = name.trim().split(/\s+/);
    return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
};

// 6 réactions standards « façon Facebook »
const REACTIONS = [
    { key: 'like', emoji: '👍', label: "J'aime" },
    { key: 'love', emoji: '❤️', label: 'Cœur' },
    { key: 'haha', emoji: '😆', label: 'Haha' },
    { key: 'wow', emoji: '😮', label: 'Waouh' },
    { key: 'sad', emoji: '😢', label: 'Triste' },
    { key: 'clap', emoji: '👏', label: 'Bravo' },
];

// Détail des réactions : QUI a réagi + AVEC QUELLE réaction.
// S'ouvre au survol (desktop) ET au tap (mobile, priorité). Overlay pour fermer au tap extérieur.
const ReactionsViewer = ({ summary, noteId }) => {
    const [open, setOpen] = useState(false);
    const items = [];
    REACTIONS.forEach((r) => {
        const detail = summary?.reactors_detail?.[r.key];
        if (detail && detail.length) {
            detail.forEach((d) => items.push({ name: d.name, avatar: d.avatar, emoji: r.emoji, label: r.label }));
        } else {
            // Repli si reactors_detail absent (ancien payload) : noms seuls
            (summary?.reactors?.[r.key] || []).forEach((name) =>
                items.push({ name, avatar: null, emoji: r.emoji, label: r.label })
            );
        }
    });
    if (items.length === 0) return null;
    return (
        <div
            className="relative"
            onPointerEnter={(e) => { if (e.pointerType === 'mouse') setOpen(true); }}
            onPointerLeave={(e) => { if (e.pointerType === 'mouse') setOpen(false); }}
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid={`wall-reactions-who-${noteId}`}
                className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-purple-600 transition-colors"
                title="Voir qui a réagi"
            >
                <Users className="w-3.5 h-3.5" />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden="true" />
                    <div
                        data-testid={`wall-reactions-popover-${noteId}`}
                        className="absolute z-40 bottom-full mb-1 left-0 min-w-[200px] max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
                    >
                        <p className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                            Qui a réagi
                        </p>
                        {items.map((it, i) => (
                            <div key={i} className="flex items-center gap-2 px-1 py-1 text-xs text-slate-700">
                                <span className="relative shrink-0">
                                    <Avatar className="h-6 w-6">
                                        {it.avatar && <AvatarImage src={it.avatar} />}
                                        <AvatarFallback className="text-[9px] bg-slate-100 text-slate-600">{initials(it.name)}</AvatarFallback>
                                    </Avatar>
                                    <span className="absolute -bottom-1 -right-1 text-[10px] leading-none">{it.emoji}</span>
                                </span>
                                <span className="flex-1 truncate">{it.name}</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const dateTimeFormatter = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const dateFormatter = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const fmtDateTime = (iso) => {
    if (!iso) return '';
    try {
        return dateTimeFormatter.format(new Date(iso));
    } catch { return ''; }
};

const fmtDate = (iso) => {
    if (!iso) return '';
    try {
        const s = iso.length === 10 ? iso + 'T00:00:00' : iso;
        return dateFormatter.format(new Date(s));
    } catch { return ''; }
};

// Mur du dossier — indépendant par module (Courtage / Travaux d’optimisation).
// Tout intervenant du dossier peut écrire. Messages du plus récent au plus ancien.
export default function ProspectWall({ prospectId, module, moduleLabel, onRead, canAttach }) {
    const { user, refreshWallUnread } = useAuth();
    const pendingKey = publicationKey(user?.id, prospectId, module);
    const initialPublication = useRef(getPublication(pendingKey)).current;
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isVP = user?.role === 'vp';
    // Modèles de messages : réservés aux admins + VP (usage pro, apporteurs n'y ont pas accès)
    const canUseTemplates = isAdmin || isVP;

    const [notes, setNotes] = useState([]);
    const [users, setUsers] = useState([]);
    const [content, setContent] = useState(initialPublication?.content || '');
    const [mentioned, setMentioned] = useState(initialPublication?.mentioned || []);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(initialPublication?.status === 'sending');
    const [deletingId, setDeletingId] = useState(null);
    const [pickerFor, setPickerFor] = useState(null);
    // Édition d'un message existant (auteur uniquement)
    const [editingId, setEditingId] = useState(null);
    const [editingContent, setEditingContent] = useState('');
    const [editingMentioned, setEditingMentioned] = useState([]);
    const [savingEditId, setSavingEditId] = useState(null);
    // Modèles de messages partagés (bibliothèque admin, lecture pour tous)
    const [templates, setTemplates] = useState([]);
    const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
    const [saveTplOpen, setSaveTplOpen] = useState(false);
    const [saveTplTitle, setSaveTplTitle] = useState('');
    const [savingTpl, setSavingTpl] = useState(false);
    const [tplLoaded, setTplLoaded] = useState(false);

    const [showSuggest, setShowSuggest] = useState(false);
    const [query, setQuery] = useState('');
    const [caretAt, setCaretAt] = useState(null);
    const taRef = useRef(null);
    const editTaRef = useRef(null);

    // Auto-resize : textarea grandit avec le contenu, jusqu'à ~260px puis scroll interne.
    // Évite les bugs de "petite fenêtre" quand on colle un long modèle ou qu'on écrit
    // un message de 20 lignes.
    const autoGrow = useCallback((el) => {
        if (!el) return;
        el.style.height = 'auto';
        const next = Math.min(el.scrollHeight, 260);
        el.style.height = `${next}px`;
    }, []);

    // Grandit le composer à chaque changement de content (insertion modèle, saisie, etc.)
    useEffect(() => {
        autoGrow(taRef.current);
    }, [content, autoGrow]);

    // Grandit la textarea d'édition quand elle apparaît ou change
    useEffect(() => {
        if (editingId) autoGrow(editTaRef.current);
    }, [editingContent, editingId, autoGrow]);
    const submitLockRef = useRef(false); // Verrou anti-double-envoi (race condition avant que `saving` re-render)
    const attachmentsRef = useRef(null);
    const requestRef = useRef(initialPublication?.request || null);
    const loadSequenceRef = useRef(0);
    const mountedRef = useRef(true);
    const [attachmentState, setAttachmentState] = useState({ count: 0, blocked: false });
    const [selectedAttachment, setSelectedAttachment] = useState(null);
    const attachmentChanged = useCallback(next => setAttachmentState(previous => previous.count === next.count && previous.blocked === next.blocked ? previous : next), []);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
    const [wallItem, setWallItem] = useState(null); // { refType, refId } — tâche/événement ouvert depuis le mur

    // Deep-link : surligner + scroller vers un message précis (depuis une notification)
    const [searchParams] = useSearchParams();
    const highlightNoteId = searchParams.get('note');
    const [flashId, setFlashId] = useState(null);

    const accent = module === 'chantier' ? 'blue' : 'amber';
    const Icon = module === 'chantier' ? HardHat : Zap;

    const load = useCallback(async () => {
        const sequence = ++loadSequenceRef.current;
        setLoading(true);
        try {
            const [nRes, uRes] = await Promise.all([
                prospectsAPI.getNotes(prospectId, module),
                prospectsAPI.getMentionableUsers(prospectId, module),
            ]);
            if (mountedRef.current && sequence === loadSequenceRef.current) {
                setNotes(nRes.data || []);
                setUsers(uRes.data || []);
            }
        } catch (e) {
            console.error('wall load error', e);
        } finally {
            if (mountedRef.current && sequence === loadSequenceRef.current) setLoading(false);
        }
    }, [prospectId, module]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const receive = ({ entry, note }) => {
            setSaving(entry?.status === 'sending');
            if (entry?.status === 'failed') {
                attachmentsRef.current?.retain(false);
                forgetPublication(pendingKey); // Le mur remonté reprend la propriété du brouillon.
            }
            if (note) {
                attachmentsRef.current?.complete(); requestRef.current = null;
                setContent(''); setMentioned([]);
                setNotes(previous => previous.some(item => item.id === note.id) ? previous : [note, ...previous]);
                load();
            }
        };
        const unsubscribe = subscribePublication(pendingKey, receive);
        if (getPublication(pendingKey)?.status === 'failed') receive({ entry: getPublication(pendingKey) });
        return unsubscribe;
    }, [pendingKey, load]);

    // === État des lieux Uatu — bouton dans le header ===
    const [statusReportLoading, setStatusReportLoading] = useState(false);
    const requestUatuStatusReport = async () => {
        setStatusReportLoading(true);
        try {
            const token = localStorage.getItem('valnexo_token') || localStorage.getItem('token');
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/prospects/${prospectId}/uatu-status-report`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ module }),
                },
            );
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Uatu n\'a pas pu générer l\'état des lieux.');
                return;
            }
            const data = await res.json();
            if (data.is_reuse) {
                toast.info(data.message || 'Aucune activité significative depuis le dernier état des lieux.');
                await load();
                setTimeout(() => {
                    const el = document.querySelector(`[data-testid="wall-msg-${data.existing_note.id}"]`);
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setFlashId(data.existing_note.id);
                        setTimeout(() => setFlashId(null), 3000);
                    }
                }, 300);
            } else {
                toast.success('Uatu a posté l\'état des lieux sur le mur.');
                await load();
                setTimeout(() => {
                    const el = document.querySelector(`[data-testid="wall-msg-${data.note.id}"]`);
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setFlashId(data.note.id);
                        setTimeout(() => setFlashId(null), 3000);
                    }
                }, 300);
            }
        } catch {
            toast.error('Erreur réseau. Réessaye.');
        } finally {
            setStatusReportLoading(false);
        }
    };

    // Une fois les messages chargés, scroller et mettre en évidence le message ciblé par la notification
    useEffect(() => {
        if (!highlightNoteId || loading) return;
        if (!notes.some((n) => n.id === highlightNoteId)) return;
        setFlashId(highlightNoteId);
        const el = document.querySelector(`[data-testid="wall-msg-${highlightNoteId}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const t = setTimeout(() => setFlashId(null), 4500);
        return () => clearTimeout(t);
    }, [notes, loading, highlightNoteId]);

    // Ouvrir le mur d'un module = le marquer comme lu (efface la pastille de cet onglet)
    useEffect(() => {
        prospectsAPI.markWallRead(prospectId, module)
            .then(() => { onRead?.(module); refreshWallUnread?.(); })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prospectId, module]);

    const onChange = (e) => {
        const val = e.target.value;
        setContent(val);
        const pos = e.target.selectionStart;
        const upto = val.slice(0, pos);
        const match = upto.match(/@([\p{L}\-]*)$/u);
        if (match) {
            setCaretAt(pos - match[0].length);
            setQuery(match[1].toLowerCase());
            setShowSuggest(true);
        } else {
            setShowSuggest(false);
            setQuery('');
            setCaretAt(null);
        }
    };

    const filtered = users.filter((u) => !query || (u.name || '').toLowerCase().includes(query)).slice(0, 6);

    // Séparation Messages / Actions (tâches & événements) — affichés côte à côte
    const actions = useMemo(() => notes.filter((n) => n.kind === 'task_event' || n.kind === 'event_event'), [notes]);
    const messages = useMemo(() => notes.filter((n) => n.kind !== 'task_event' && n.kind !== 'event_event'), [notes]);

    const pickUser = (u) => {
        if (caretAt == null) return;
        const before = content.slice(0, caretAt);
        const after = content.slice(caretAt + 1 + query.length);
        const next = `${before}@${u.name} ${after}`;
        setContent(next);
        setShowSuggest(false);
        setQuery('');
        setCaretAt(null);
        if (!mentioned.some((m) => m.id === u.id)) setMentioned((prev) => [...prev, u]);
        setTimeout(() => taRef.current?.focus(), 0);
    };

    const removeMention = (id) => setMentioned((prev) => prev.filter((m) => m.id !== id));

    const submit = async () => {
        if ((!content.trim() && !attachmentState.count) || attachmentState.blocked) return;
        // Verrou synchrone : bloque tout second appel même avant que setSaving(true) ne se propage
        if (submitLockRef.current || saving || getPublication(pendingKey)?.status === 'sending') return;
        submitLockRef.current = true;
        const stillMentioned = mentioned.filter((m) => content.includes(`@${m.name}`));
        const attachmentIds = attachmentsRef.current?.ids() || [];
        const fingerprint = JSON.stringify([prospectId, module, content.trim(), attachmentIds, stillMentioned.map(m => m.id)]);
        if (requestRef.current?.fingerprint !== fingerprint) requestRef.current = { fingerprint, id: crypto.randomUUID() };
        const publication = { status: 'sending', content, mentioned, files: attachmentsRef.current?.snapshot() || [], request: requestRef.current };
        attachmentsRef.current?.retain(true);
        updatePublication(pendingKey, publication);
        const complete = note => {
            updatePublication(pendingKey, null, note);
            refreshWallUnread(); toast.success('Message publié');
        };
        try {
            const res = await prospectsAPI.addNote(prospectId, {
                content: content.trim(),
                mentioned_user_ids: stillMentioned.map((m) => m.id),
                module,
                attachment_ids: attachmentIds,
                client_request_id: requestRef.current.id,
            });
            complete(res.data.note);
        } catch (e) {
            try {
                const { data } = await prospectsAPI.getNotes(prospectId, module);
                const delivered = data.find(note => note.client_request_id === publication.request.id && note.author_id === user.id);
                if (delivered) {
                    complete(delivered); return;
                }
            } catch { /* Le brouillon reste disponible pour réessayer. */ }
            updatePublication(pendingKey, { ...publication, status: 'failed' });
            toast.error(e.response?.data?.detail || 'Publication impossible. Votre brouillon est conservé sur ce mur.');
        } finally {
            if (mountedRef.current) setSaving(false);
            submitLockRef.current = false;
        }
    };

    const handleDelete = useCallback(async (noteId) => {
        if (!confirm('Supprimer ce message du mur ?')) return;
        setDeletingId(noteId);
        try {
            await prospectsAPI.deleteNote(prospectId, noteId);
            setNotes((prev) => prev.filter((n) => n.id !== noteId));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setDeletingId(null);
        }
    }, [prospectId]);

    const handleReact = useCallback(async (noteId, reaction) => {
        setPickerFor(null);
        // Optimiste : recharger la synthèse renvoyée par l'API
        try {
            const res = await prospectsAPI.reactToNote(prospectId, noteId, reaction);
            setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, reactions_summary: res.data } : n)));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la réaction');
        }
    }, [prospectId]);

    // ============ Édition d'un message existant ============
    const startEdit = useCallback((note) => {
        setEditingId(note.id);
        setEditingContent(note.content || '');
        // Reconstitue les mentions existantes à partir de mentioned_user_ids + users
        const existing = (note.mentioned_user_ids || [])
            .map((mid) => users.find((u) => u.id === mid))
            .filter(Boolean);
        setEditingMentioned(existing);
    }, [users]);
    const cancelEdit = useCallback(() => {
        setEditingId(null);
        setEditingContent('');
        setEditingMentioned([]);
    }, []);
    const saveEdit = useCallback(async () => {
        if (!editingId || (!editingContent.trim() && !notes.find(note => note.id === editingId)?.attachments?.length) || savingEditId) return;
        const stillMentioned = editingMentioned.filter((m) => editingContent.includes(`@${m.name}`));
        setSavingEditId(editingId);
        try {
            const res = await prospectsAPI.updateNote(prospectId, editingId, {
                content: editingContent.trim(),
                mentioned_user_ids: stillMentioned.map((m) => m.id),
            });
            const updated = res.data?.note;
            if (updated) {
                setNotes((prev) => prev.map((n) => (n.id === editingId ? { ...n, ...updated } : n)));
            }
            toast.success('Message modifié');
            cancelEdit();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la modification');
        } finally {
            setSavingEditId(null);
        }
    }, [editingId, editingContent, notes, savingEditId, editingMentioned, prospectId, cancelEdit]);

    // ============ Modèles de messages ============
    const loadTemplates = useCallback(async () => {
        try {
            const res = await wallAPI.listTemplates();
            setTemplates(res.data?.templates || []);
            setTplLoaded(true);
        } catch (e) {
            console.error('templates load error', e);
        }
    }, []);
    const openTemplatePicker = () => {
        if (!tplLoaded) loadTemplates();
        setTemplatePickerOpen(true);
    };
    const applyTemplate = (tpl) => {
        // Insère le contenu du modèle dans le champ de rédaction. Les @noms cités
        // dans le modèle sont libres à changer par l'utilisateur avant publication.
        setContent(tpl.content || '');
        setMentioned([]); // les mentions seront re-dérivées si les @noms correspondent à des users du dossier
        setTemplatePickerOpen(false);
        setTimeout(() => taRef.current?.focus(), 0);
    };
    const openSaveTemplate = () => {
        if (!content.trim()) {
            toast.error('Écris d\'abord un message avant de l\'enregistrer comme modèle');
            return;
        }
        setSaveTplTitle('');
        setSaveTplOpen(true);
    };
    const saveAsTemplate = async () => {
        const title = saveTplTitle.trim();
        if (!title) {
            toast.error('Donne un titre au modèle');
            return;
        }
        setSavingTpl(true);
        try {
            const res = await wallAPI.createTemplate({ title, content: content.trim() });
            const tpl = res.data?.template;
            if (tpl) setTemplates((prev) => [tpl, ...prev].sort((a, b) => (a.title || '').localeCompare(b.title || '')));
            toast.success(`Modèle « ${title} » enregistré`);
            setSaveTplOpen(false);
            setSaveTplTitle('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
        } finally {
            setSavingTpl(false);
        }
    };
    const deleteTemplate = async (tplId, tplTitle) => {
        if (!confirm(`Supprimer le modèle « ${tplTitle} » définitivement ?`)) return;
        try {
            await wallAPI.deleteTemplate(tplId);
            setTemplates((prev) => prev.filter((t) => t.id !== tplId));
            toast.success('Modèle supprimé');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const renderContent = useCallback((text, names) => {
        if (!text) return text;
        const sorted = names && names.length ? [...names].sort((a, b) => b.length - a.length) : [];
        // Fabrique une regex pour mentionner les noms connus
        const parts = [];
        let key = 0;
        if (sorted.length > 0) {
            const regex = new RegExp(`@(${sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
            let lastIndex = 0;
            let m;
            while ((m = regex.exec(text)) !== null) {
                if (m.index > lastIndex) parts.push({ type: 'text', value: text.slice(lastIndex, m.index) });
                parts.push({ type: 'mention', value: m[0] });
                lastIndex = m.index + m[0].length;
            }
            if (lastIndex < text.length) parts.push({ type: 'text', value: text.slice(lastIndex) });
        } else {
            parts.push({ type: 'text', value: text });
        }
        return parts.map((p) => {
            if (p.type === 'mention') {
                return <span key={key++} className="text-purple-700 font-semibold bg-purple-50 rounded px-1">{p.value}</span>;
            }
            return <span key={key++}>{linkifyText(p.value, (u, i) => `pw-${key}-${i}`)}</span>;
        });
    }, []);

    return (
        <Card data-testid={`wall-${module}`} data-prospect-id={prospectId} className={`border-${accent}-200`}>
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                    <MessageSquareText className={`w-5 h-5 text-${accent}-600`} />
                    Mur du dossier
                    <Badge variant="outline" className="ml-1 gap-1 text-[11px]">
                        <Icon className="w-3 h-3" />{moduleLabel}
                    </Badge>
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={requestUatuStatusReport}
                        disabled={statusReportLoading}
                        data-testid={`wall-uatu-status-report-${module}`}
                        className="ml-auto gap-1.5 border-purple-300 text-purple-700 hover:bg-purple-50"
                        title="Uatu génère un état des lieux complet du dossier et le poste sur ce mur (2 modules analysés)"
                    >
                        {statusReportLoading ? (
                            <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Uatu observe le dossier…
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-3.5 h-3.5" />
                                État des lieux Uatu
                            </>
                        )}
                    </Button>
                </CardTitle>
                <p className="text-xs text-slate-500">
                    Espace d'échange visible par tous les intervenants de ce dossier. Tapez{' '}
                    <span className="font-mono text-purple-600">@</span> pour mentionner et notifier quelqu'un.
                </p>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Hashtags # du module (tout le monde peut taguer ; # créés par les admins) */}
                <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-2">
                    <span className="text-[11px] font-medium text-slate-500 shrink-0">Hashtags :</span>
                    <HashtagPicker prospectId={prospectId} module={module} />
                </div>
                {/* Composer */}
                <div className="rounded-lg border border-slate-200 bg-white p-2 space-y-2 focus-within:border-purple-300 focus-within:ring-1 focus-within:ring-purple-200 transition-colors" data-testid={`wall-message-composer-${module}`}>
                <div className="relative">
                    <textarea
                        ref={taRef}
                        value={content}
                        onChange={onChange}
                        placeholder="Écrire un message sur le mur…  (@ pour mentionner)"
                        disabled={saving}
                        rows={2}
                        data-testid={`wall-composer-${module}`}
                        style={{ minHeight: '60px', maxHeight: '260px' }}
                        className="block w-full resize-none overflow-y-auto border-0 bg-transparent px-2 py-2 text-sm leading-relaxed focus:outline-none focus:ring-0"
                    />
                    {showSuggest && filtered.length > 0 && (
                        <div data-testid={`wall-mention-suggestions-${module}`}
                            className="absolute z-20 mt-1 w-72 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                            {filtered.map((u) => (
                                <button key={u.id} type="button" data-testid={`wall-mention-option-${u.id}`}
                                    onClick={() => pickUser(u)}
                                    className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-purple-50">
                                    <Avatar className="h-6 w-6">
                                        {u.avatar && <AvatarImage src={u.avatar} />}
                                        <AvatarFallback className="bg-purple-100 text-purple-700 text-[10px]">{initials(u.name)}</AvatarFallback>
                                    </Avatar>
                                    <span className="text-sm text-slate-700">{u.name}</span>
                                    <Badge variant="outline" className="ml-auto text-[10px] capitalize">{u.role}</Badge>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {mentioned.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {mentioned.map((m) => (
                            <Badge key={m.id} className="bg-purple-100 text-purple-700 gap-1 hover:opacity-90">
                                <AtSign className="w-3 h-3" />{m.name}
                                <button type="button" onClick={() => removeMention(m.id)} disabled={saving} aria-label={`Retirer la mention de ${m.name}`} data-testid={`wall-remove-mention-${module}-${m.id}`} className="ml-1 hover:text-purple-900">×</button>
                            </Badge>
                        ))}
                    </div>
                )}

                {(canAttach ?? (!user?.is_auditor || isAdmin)) && <WallAttachmentComposer ref={attachmentsRef} prospectId={prospectId} module={module}
                    initialFiles={initialPublication?.files} initiallyRetained={initialPublication?.status === 'sending'}
                    disabled={saving} onChange={attachmentChanged} onOpen={setSelectedAttachment} />}
                <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-2" data-testid={`wall-composer-toolbar-${module}`}>
                    <div className="flex min-w-0 flex-wrap items-center gap-1">
                        {(canAttach ?? (!user?.is_auditor || isAdmin)) && <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-slate-500 hover:text-purple-700" disabled={saving || attachmentState.count >= 5}
                            onClick={() => attachmentsRef.current?.choose()} title="Joindre un fichier · 5 fichiers maximum, 25 Mo chacun" aria-label="Joindre un fichier" data-testid={`wall-attach-${module}`}><Paperclip className="h-4 w-4" /></Button>}
                        {/* Bibliothèque de modèles — visible uniquement pour Admins + VP */}
                        {canUseTemplates && (
                        <Popover open={templatePickerOpen} onOpenChange={setTemplatePickerOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={openTemplatePicker}
                                    className="h-8 w-8 text-slate-500"
                                    title="Utiliser un modèle"
                                    aria-label="Utiliser un modèle"
                                    disabled={saving}
                                    data-testid={`wall-template-picker-${module}`}
                                >
                                    <LayoutTemplate className="w-4 h-4" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-[min(380px,calc(100vw-32px))] p-0 shadow-xl" sideOffset={8}>
                                <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                                    <p className="text-xs font-semibold text-slate-700">Bibliothèque de modèles</p>
                                    {isAdmin && (
                                        <span className="text-[10px] text-slate-400">Admin : gérables ici</span>
                                    )}
                                </div>
                                <div className="max-h-[380px] overflow-y-auto p-2 space-y-1.5">
                                    {templates.length === 0 ? (
                                        <p className="text-xs text-slate-400 text-center py-6">
                                            {tplLoaded ? 'Aucun modèle enregistré pour le moment.' : 'Chargement…'}
                                        </p>
                                    ) : templates.map((tpl) => (
                                        <div
                                            key={tpl.id}
                                            className="group rounded-lg border border-slate-200 hover:border-purple-300 hover:bg-purple-50/30 transition-colors overflow-hidden"
                                            data-testid={`wall-template-item-${tpl.id}`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => applyTemplate(tpl)}
                                                className="w-full text-left p-2.5"
                                            >
                                                <p className="text-sm font-semibold text-slate-800 line-clamp-1">{tpl.title}</p>
                                                <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5 whitespace-pre-wrap">{tpl.content}</p>
                                                {tpl.created_by_name && (
                                                    <p className="text-[10px] text-slate-400 mt-1">par {tpl.created_by_name}</p>
                                                )}
                                            </button>
                                            {isAdmin && (
                                                <div className="flex justify-end gap-1 px-2 pb-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteTemplate(tpl.id, tpl.title)}
                                                        className="text-[10px] text-red-500 hover:text-red-700 gap-0.5 inline-flex items-center"
                                                        data-testid={`wall-template-delete-${tpl.id}`}
                                                    >
                                                        <Trash2 className="w-3 h-3" /> Supprimer
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </PopoverContent>
                        </Popover>
                        )}

                        {/* Bouton : enregistrer le message courant comme modèle (admins uniquement) */}
                        {isAdmin && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={openSaveTemplate}
                                disabled={saving || !content.trim()}
                                className="h-8 w-8 text-slate-500"
                                data-testid={`wall-template-save-${module}`}
                                title="Enregistrer ce message comme modèle réutilisable"
                                aria-label="Enregistrer comme modèle"
                            >
                                <BookmarkPlus className="w-4 h-4" />
                            </Button>
                        )}
                    </div>

                    <Button onClick={submit} disabled={saving || attachmentState.blocked || (!content.trim() && !attachmentState.count)} size="sm" data-testid={`wall-submit-${module}`}
                        className="shrink-0 bg-purple-600 hover:bg-purple-700 gap-1">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Publier
                    </Button>
                </div>
                </div>

                {/* Messages + Actions côte à côte */}
                {/* La saisie et les transferts ne recalculent pas l'historique. */}
                {useMemo(() => (
                <div className="grid grid-cols-1 gap-4 pt-2 border-t border-slate-100 lg:grid-cols-3">
                    {/* Colonne Messages */}
                    <div className="lg:col-span-2">
                        <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                            <MessageSquareText className="w-3.5 h-3.5 text-purple-500" /> Messages
                            {messages.length > 0 && <span className="text-[10px] text-slate-400">({messages.length})</span>}
                        </p>
                        <div className="space-y-3 max-h-96 overflow-y-auto pr-1" style={{ contain: 'layout paint' }} data-testid={`wall-thread-${module}`}>
                    {loading ? (
                        <p className="text-sm text-slate-400 text-center py-4">Chargement…</p>
                    ) : messages.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">Aucun message pour le moment. Lancez la discussion !</p>
                    ) : (
                        messages.map((n) => {
                            const canDelete = isAdmin || n.author_id === user?.id;
                            const canEdit = n.author_id === user?.id && n.kind !== 'task_event' && n.kind !== 'event_event' && n.kind !== 'uatu_status_report';
                            const isEditing = editingId === n.id;
                            const isTaskEvent = n.kind === 'task_event';
                            const isCalEvent = n.kind === 'event_event';
                            const isUatuReport = n.kind === 'uatu_status_report';
                            const isSystem = isTaskEvent || isCalEvent || isUatuReport;

                            // Rendu SPÉCIAL pour l'état des lieux Uatu
                            if (isUatuReport) {
                                const requester = n.metadata?.requested_by_name || 'un utilisateur';
                                return (
                                    <div
                                        key={n.id}
                                        data-testid={`wall-msg-${n.id}`}
                                        className={`rounded-xl border-2 p-4 transition-all ${
                                            flashId === n.id
                                                ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-300 animate-pulse'
                                                : 'border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50/40'
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shrink-0 shadow-sm">
                                                <Sparkles className="w-5 h-5 text-white" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-baseline gap-2 flex-wrap">
                                                    <span className="text-sm font-bold text-purple-900">Uatu · État des lieux</span>
                                                    <Badge className="bg-purple-100 text-purple-700 border-purple-300 text-[10px]">
                                                        demandé par {requester}
                                                    </Badge>
                                                    <span className="text-xs text-slate-500 ml-auto">{fmtDateTime(n.created_at)}</span>
                                                    {isAdmin && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDelete(n.id)}
                                                            disabled={deletingId === n.id}
                                                            data-testid={`wall-delete-${n.id}`}
                                                            className="text-slate-300 hover:text-red-500 transition-colors"
                                                            title="Supprimer"
                                                        >
                                                            {deletingId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                                        </button>
                                                    )}
                                                </div>
                                                <UatuMarkdown content={n.content} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <WallMessageFrame key={n.id} deferred={notes.length > 40 && flashId !== n.id && !isEditing} estimatedHeight={90 + Math.min(n.attachments?.length || 0, 5) * 58} className={`flex gap-3 group transition-colors ${flashId === n.id ? 'ring-2 ring-purple-400 rounded-lg bg-purple-50/70 p-2 animate-pulse' : isTaskEvent ? 'rounded-lg border border-amber-200 bg-amber-50/60 p-2' : isCalEvent ? 'rounded-lg border border-blue-200 bg-blue-50/60 p-2' : ''}`} data-testid={`wall-msg-${n.id}`}>
                                    <Avatar className="h-8 w-8 shrink-0">
                                        {n.author_avatar && <AvatarImage src={n.author_avatar} />}
                                        <AvatarFallback className="bg-slate-100 text-slate-600 text-[11px]">{initials(n.author_name)}</AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                            <span className="min-w-0 break-words text-sm font-semibold text-slate-800" data-testid={`wall-msg-author-${n.id}`}>{n.author_name}</span>
                                            {isTaskEvent && (
                                                <Badge className="bg-amber-100 text-amber-700 text-[10px] gap-1" data-testid={`wall-task-badge-${n.id}`}>
                                                    <CheckSquare className="w-3 h-3" />Tâche
                                                </Badge>
                                            )}
                                            {isCalEvent && (
                                                <Badge className="bg-blue-100 text-blue-700 text-[10px] gap-1" data-testid={`wall-event-badge-${n.id}`}>
                                                    <CalendarClock className="w-3 h-3" />Événement
                                                </Badge>
                                            )}
                                            {isSystem && n.pinned_date && (
                                                <Badge variant="outline" className="text-[10px] gap-1 border-slate-300 text-slate-600" data-testid={`wall-planned-date-${n.id}`}>
                                                    <CalendarClock className="w-3 h-3" />
                                                    {isTaskEvent ? 'Échéance' : 'Prévu'} {fmtDate(n.pinned_date)}
                                                </Badge>
                                            )}
                                            {isTaskEvent && n.task_overdue && (
                                                <Badge className="text-[10px] gap-1 bg-red-100 text-red-700 hover:bg-red-100" data-testid={`wall-task-overdue-${n.id}`}>
                                                    <AlertTriangle className="w-3 h-3" />
                                                    {n.task_days_overdue >= 1 ? `${n.task_days_overdue} j de retard` : 'En retard'}
                                                </Badge>
                                            )}
                                            <span className="text-xs text-slate-400" data-testid={`wall-msg-date-${n.id}`}>{fmtDateTime(n.created_at)}</span>
                                            {n.edited_at && (
                                                <span className="text-[10px] text-slate-400 italic" title={`Modifié le ${fmtDateTime(n.edited_at)}`}>modifié</span>
                                            )}
                                            <div className="ml-auto flex items-center gap-1">
                                                {canEdit && !isEditing && (
                                                    <button
                                                        type="button"
                                                        onClick={() => startEdit(n)}
                                                        data-testid={`wall-edit-${n.id}`}
                                                        className="text-slate-300 hover:text-purple-600 transition-colors opacity-0 group-hover:opacity-100"
                                                        title="Modifier"
                                                    >
                                                        <Pencil className="w-3.5 h-3.5" />
                                                    </button>
                                                )}
                                                {canDelete && !isEditing && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(n.id)}
                                                        disabled={deletingId === n.id}
                                                        data-testid={`wall-delete-${n.id}`}
                                                        className="text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                                                        title="Supprimer"
                                                    >
                                                        {deletingId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {isSystem && n.ref_id ? (
                                            <button
                                                type="button"
                                                onClick={() => setWallItem({ refType: n.ref_type, refId: n.ref_id })}
                                                data-testid={`wall-open-item-${n.id}`}
                                                className="block w-full text-left text-sm text-slate-700 whitespace-pre-wrap [overflow-wrap:anywhere] mt-0.5 hover:underline decoration-dotted underline-offset-4 cursor-pointer"
                                                title={n.ref_type === 'event' ? "Ouvrir l'événement" : 'Ouvrir la tâche'}
                                            >
                                                {n.content}
                                                <span className="ml-1 text-[11px] font-medium text-blue-600">— ouvrir ›</span>
                                            </button>
                                        ) : isEditing ? (
                                            <div className="mt-1 space-y-2">
                                                <textarea
                                                    ref={editTaRef}
                                                    value={editingContent}
                                                    onChange={(e) => setEditingContent(e.target.value)}
                                                    rows={3}
                                                    autoFocus
                                                    data-testid={`wall-edit-textarea-${n.id}`}
                                                    style={{ minHeight: '80px', maxHeight: '260px' }}
                                                    className="w-full resize-none overflow-y-auto rounded-lg border border-purple-300 px-3 py-2 text-sm leading-relaxed focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-300"
                                                />
                                                {editingMentioned.length > 0 && (
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {editingMentioned.map((m) => (
                                                            <Badge key={m.id} className="bg-purple-100 text-purple-700 gap-1">
                                                                <AtSign className="w-3 h-3" />{m.name}
                                                                <button type="button" onClick={() => setEditingMentioned((prev) => prev.filter((x) => x.id !== m.id))} className="ml-1 hover:text-purple-900">×</button>
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                )}
                                                <div className="flex items-center gap-2 justify-end">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={cancelEdit}
                                                        disabled={savingEditId === n.id}
                                                        className="gap-1"
                                                        data-testid={`wall-edit-cancel-${n.id}`}
                                                    >
                                                        <X className="w-3.5 h-3.5" /> Annuler
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        onClick={saveEdit}
                                                        disabled={(!editingContent.trim() && !n.attachments?.length) || savingEditId === n.id}
                                                        className="bg-purple-600 hover:bg-purple-700 gap-1"
                                                        data-testid={`wall-edit-save-${n.id}`}
                                                    >
                                                        {savingEditId === n.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                                        Enregistrer
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-sm text-slate-700 whitespace-pre-wrap [overflow-wrap:anywhere] mt-0.5">{renderContent(n.content, n.mentioned_names)}</p>
                                        )}
                                        {n.mentioned_names?.length > 0 && (
                                            <p className="text-[11px] text-slate-400 mt-1">Mentionné(s) par email : {n.mentioned_names.join(', ')}</p>
                                        )}
                                        {/* Barre de réactions « façon Facebook » */}
                                        <WallAttachmentList prospectId={prospectId} noteId={n.id} files={n.attachments} onOpen={setSelectedAttachment} />
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <div className="relative">
                                                <button
                                                    type="button"
                                                    onClick={() => setPickerFor(pickerFor === n.id ? null : n.id)}
                                                    data-testid={`wall-react-toggle-${n.id}`}
                                                    className="text-[11px] text-slate-400 hover:text-purple-600 transition-colors flex items-center gap-1"
                                                    title="Réagir"
                                                >
                                                    <span className="text-sm">🙂</span> Réagir
                                                </button>
                                                {pickerFor === n.id && (
                                                    <div
                                                        data-testid={`wall-react-picker-${n.id}`}
                                                        className="absolute z-30 bottom-full mb-1 left-0 flex gap-0.5 rounded-full border border-slate-200 bg-white px-2 py-1 shadow-lg"
                                                    >
                                                        {REACTIONS.map((r) => (
                                                            <button
                                                                key={r.key}
                                                                type="button"
                                                                onClick={() => handleReact(n.id, r.key)}
                                                                data-testid={`wall-react-${r.key}-${n.id}`}
                                                                title={r.label}
                                                                className={`text-lg leading-none p-1 rounded-full transition-transform hover:scale-125 ${n.reactions_summary?.my === r.key ? 'bg-purple-100' : 'hover:bg-slate-100'}`}
                                                            >
                                                                {r.emoji}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            {/* Pastilles agrégées des réactions existantes */}
                                            {n.reactions_summary?.total > 0 && (
                                                <div className="flex items-center gap-1 flex-wrap" data-testid={`wall-reactions-${n.id}`}>
                                                    {REACTIONS.filter((r) => n.reactions_summary.counts?.[r.key]).map((r) => {
                                                        const reactors = n.reactions_summary.reactors?.[r.key] || [];
                                                        const detail = n.reactions_summary.reactors_detail?.[r.key] || [];
                                                        const shown = detail.slice(0, 3);
                                                        const extra = detail.length - shown.length;
                                                        return (
                                                            <button
                                                                key={r.key}
                                                                type="button"
                                                                onClick={() => handleReact(n.id, r.key)}
                                                                data-testid={`wall-reaction-pill-${r.key}-${n.id}`}
                                                                title={reactors.join(', ')}
                                                                className={`flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] transition-colors ${n.reactions_summary.my === r.key ? 'border-purple-300 bg-purple-50 text-purple-700' : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
                                                            >
                                                                <span className="text-xs">{r.emoji}</span>
                                                                {shown.length > 0 && (
                                                                    <span className="flex items-center -space-x-1.5">
                                                                        {shown.map((d, di) => (
                                                                            <Avatar key={di} className="h-4 w-4 ring-1 ring-white">
                                                                                {d.avatar && <AvatarImage src={d.avatar} />}
                                                                                <AvatarFallback className="text-[7px] bg-slate-200 text-slate-600">{initials(d.name)}</AvatarFallback>
                                                                            </Avatar>
                                                                        ))}
                                                                    </span>
                                                                )}
                                                                <span>{extra > 0 ? `+${extra}` : n.reactions_summary.counts[r.key]}</span>
                                                            </button>
                                                        );
                                                    })}
                                                    <ReactionsViewer summary={n.reactions_summary} noteId={n.id} />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </WallMessageFrame>
                            );
                        })
                    )}
                        </div>
                    </div>
                    {/* Colonne Actions (tâches & événements) — cliquables */}
                    <div className="lg:col-span-1">
                        <p className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
                            <CheckSquare className="w-3.5 h-3.5 text-amber-500" /> Actions
                            {actions.length > 0 && <span className="text-[10px] text-slate-400">({actions.length})</span>}
                        </p>
                        <div className="space-y-2 max-h-96 overflow-y-auto pr-1" data-testid={`wall-actions-${module}`}>
                            {actions.length === 0 ? (
                                <p className="text-xs text-slate-400 py-3 text-center">Aucune action planifiée.</p>
                            ) : actions.map((n) => {
                                const isTask = n.kind === 'task_event';
                                const automatic = automaticRelanceDisplay(n);
                                return (
                                    <button
                                        key={n.id}
                                        type="button"
                                        onClick={() => n.ref_id && setWallItem({ refType: n.ref_type, refId: n.ref_id })}
                                        data-testid={`wall-action-${n.id}`}
                                        className={`w-full text-left rounded-lg border p-2.5 transition-colors ${isTask ? 'border-amber-200 bg-amber-50/60 hover:bg-amber-100/60' : 'border-blue-200 bg-blue-50/60 hover:bg-blue-100/60'}`}
                                    >
                                        <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                            {isTask ? (
                                                <>
                                                    <Badge className="bg-amber-100 text-amber-700 text-[10px] gap-1"><CheckSquare className="w-3 h-3" />Tâche</Badge>
                                                    {n.task_status && (
                                                        <Badge data-testid={`wall-task-status-${n.id}`} className={`text-[10px] ${TASK_STATUS_BADGE[n.task_status]?.cls || 'bg-slate-100 text-slate-600'}`}>
                                                            {TASK_STATUS_BADGE[n.task_status]?.label || n.task_status}
                                                        </Badge>
                                                    )}
                                                </>
                                            ) : (
                                                <Badge className="bg-blue-100 text-blue-700 text-[10px] gap-1"><CalendarClock className="w-3 h-3" />Événement</Badge>
                                            )}
                                            {n.pinned_date && (
                                                <Badge variant="outline" className="text-[10px] gap-1 border-slate-300 text-slate-600">
                                                    <CalendarClock className="w-3 h-3" />{isTask ? 'Échéance' : 'Prévu'} {fmtDate(n.pinned_date)}
                                                </Badge>
                                            )}
                                            {isTask && n.task_overdue && (
                                                <Badge className="text-[10px] gap-1 bg-red-100 text-red-700"><AlertTriangle className="w-3 h-3" />{n.task_days_overdue >= 1 ? `${n.task_days_overdue} j` : 'Retard'}</Badge>
                                            )}
                                        </div>
                                        {automatic && <p className="mb-1 text-xs font-semibold text-slate-800 [overflow-wrap:anywhere]" data-testid={`wall-action-title-${n.id}`}>{automatic.title}</p>}
                                        <p className="text-xs text-slate-700 line-clamp-2" data-testid={`wall-action-content-${n.id}`}>{automatic ? automatic.content : n.content}</p>
                                        <p className="text-[10px] text-slate-400 mt-1">
                                            <span className={automatic ? 'font-medium text-slate-600' : undefined} data-testid={`wall-action-author-${n.id}`}>{automatic ? automatic.author : n.author_name}</span>
                                            {' · '}<span data-testid={`wall-action-date-${n.id}`}>{fmtDateTime(n.created_at)}</span> <span className="text-blue-600 font-medium">— ouvrir ›</span>
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
                ), [messages, actions, module, loading, isAdmin, user?.id, editingId, editingContent, editingMentioned, savingEditId, deletingId, pickerFor, flashId,
                    handleDelete, handleReact, startEdit, cancelEdit, saveEdit, renderContent, prospectId])}
            </CardContent>

            <WallItemDialog
                open={!!wallItem}
                refType={wallItem?.refType}
                refId={wallItem?.refId}
                onClose={() => setWallItem(null)}
                onChanged={load}
            />

            {/* Dialog — Enregistrer un modèle de message (admins uniquement) */}
            {selectedAttachment && <Suspense fallback={<span className="sr-only" role="status" data-testid="wall-viewer-loading">Chargement de l’aperçu</span>}>
                <WallAttachmentViewer prospectId={prospectId} file={selectedAttachment} onClose={() => setSelectedAttachment(null)} />
            </Suspense>}
            <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="wall-template-save-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <BookmarkPlus className="w-5 h-5 text-purple-600" />
                            Enregistrer comme modèle
                        </DialogTitle>
                        <DialogDescription>
                            Ce message sera enregistré dans la bibliothèque partagée et pourra être réutilisé sur n&apos;importe quel dossier par les admins et les VP.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <label className="text-xs font-semibold text-slate-600 mb-1 block">Titre du modèle</label>
                            <Input
                                autoFocus
                                value={saveTplTitle}
                                onChange={(e) => setSaveTplTitle(e.target.value)}
                                placeholder="Ex : Relance étude 48h"
                                maxLength={80}
                                data-testid="wall-template-title-input"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && saveTplTitle.trim() && content.trim() && !savingTpl) {
                                        e.preventDefault();
                                        saveAsTemplate();
                                    }
                                }}
                            />
                            <p className="text-[10px] text-slate-400 mt-1">{saveTplTitle.length} / 80</p>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-slate-600 mb-1 block">Aperçu du message</label>
                            <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-sm text-slate-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                                {content || <span className="text-slate-400">Aucun message rédigé</span>}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSaveTplOpen(false)} disabled={savingTpl}>
                            Annuler
                        </Button>
                        <Button
                            onClick={saveAsTemplate}
                            disabled={!saveTplTitle.trim() || !content.trim() || savingTpl}
                            className="bg-purple-600 hover:bg-purple-700 gap-1"
                            data-testid="wall-template-save-confirm"
                        >
                            {savingTpl ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4" />}
                            Enregistrer le modèle
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
